import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, Sliders, Layers, Film, MapPin, Phone, Mail, Star, Check } from "lucide-react";
import api from "@/lib/api";
import { STUDIO_PHOTO, IMG, STUDIO_INFO } from "@/lib/brand";
import { Section, PageHeader, Eyebrow, SpecialRequest } from "@/components/common";

const REASONS = [
  ["🎙️ Studio professionnel", "Un espace pensé pour obtenir des prises vocales propres et professionnelles."],
  ["🎚️ Qualité audio", "Enregistrement, mixage et mastering pour donner une vraie finition à tes morceaux."],
  ["🤝 Accompagnement", "Un accompagnement adapté à ton projet et à ton niveau."],
  ["🚀 Prêt pour la sortie", "Prépare ton morceau pour sa diffusion et son exploitation musicale."],
];

function money(n) { return `${Number(n || 0).toFixed(0)} €`; }

export default function Studio() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);
  const formule = services.find((s) => s.category === "Formule");
  const others = services.filter((s) => s.category !== "Formule");

  return (
    <div data-testid="studio-page">
      <PageHeader eyebrow="AL Music Records — Studio" title="Enregistre. Mixe. Masterise. Fais sortir ton son." subtitle="Donne vie à tes projets musicaux dans un studio professionnel à Allonnes, près du Mans." image={IMG.micRed} />

      <Section className="py-10">
        <div className="flex flex-wrap gap-4 items-center">
          <Link to="/booking-studio" data-testid="studio-reserve-btn" className="btn-gold">🔥 RÉSERVER MA SESSION <ArrowRight size={18} /></Link>
          <span className="text-gold font-display font-bold text-lg">À partir de 35 € HT/h · 42 € TTC</span>
        </div>
      </Section>

      {/* SERVICES + PRIX */}
      <Section className="py-10">
        <Eyebrow>Nos services studio</Eyebrow>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl mb-8">Choisis ta prestation et réserve</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {others.map((s) => (
            <div key={s.id} className="card-dark p-6 flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display font-bold text-lg">{s.name}</h3>
                <div className="shrink-0 text-right">
                  <div className="font-display font-black text-gold text-xl">{money(s.price)} HT{s.price_type === "hourly" ? "/h" : ""}</div>
                  <div className="text-xs text-slate-400">{money(s.price * (1 + Number(s.vat || 0) / 100))} TTC{s.price_type === "hourly" ? "/h" : ""}</div>
                </div>
              </div>
              {s.conditions && <p className="text-xs text-amber-400 mt-1">{s.conditions}</p>}
              {s.description && <p className="text-sm text-slate-400 mt-3 flex-1">{s.description}</p>}
              <Link to="/booking-studio" data-testid={`studio-book-${s.id}`} className="btn-gold !py-2.5 mt-5 justify-center text-sm">RÉSERVER</Link>
            </div>
          ))}
        </div>
      </Section>

      {/* FORMULE */}
      {formule && (
        <Section className="py-10">
          <div className="relative overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/10 via-[#121216] to-red-900/10 p-8 sm:p-12">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-300 border border-amber-400/30 rounded-full px-3 py-1"><Star size={13} /> Formule Studio</div>
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center mt-5">
              <div>
                <h2 className="font-display font-black text-3xl sm:text-4xl">PRISE DE VOIX + MIX + MASTER</h2>
                <p className="text-slate-300 mt-2">2 heures de prise de voix + mix vocal + mastering</p>
                <ul className="mt-5 space-y-2 text-sm text-slate-300">
                  {["2 heures de prise de voix", "Mix vocal", "Mastering", "Accompagnement studio", "Fichier final prêt à être diffusé"].map((x) => (
                    <li key={x} className="flex items-center gap-2"><Check size={16} className="text-emerald-400" /> {x}</li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="font-display font-black text-6xl text-gold">190 € <span className="text-2xl align-top">HT</span></div>
                <div className="text-sm text-slate-300 mt-1">228 € TTC</div>
                <Link to="/booking-studio" data-testid="studio-formule-btn" className="btn-gold mt-6 justify-center w-full">🔥 RÉSERVER LA FORMULE</Link>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* PHOTO + SERVICES */}
      <Section className="py-14">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <img src={STUDIO_PHOTO} alt="Studio AL Music Records" className="w-full h-full object-cover" data-testid="studio-photo" />
          </div>
          <div>
            <Eyebrow>Pourquoi AL Music Records ?</Eyebrow>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl mb-8">Ton talent. Notre studio. Ton avenir.</h2>
            <div className="space-y-5">
              {REASONS.map(([t, d]) => (
                <div key={t}><h3 className="font-display font-bold text-lg">{t}</h3><p className="text-slate-400 text-sm mt-1">{d}</p></div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* INFOS STUDIO */}
      <Section className="py-10">
        <div className="card-dark overflow-hidden grid md:grid-cols-2">
          <div className="p-8">
            <Eyebrow>Infos studio</Eyebrow>
            <h2 className="font-display font-extrabold text-2xl mb-6">Nous trouver</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3"><MapPin className="text-gold mt-0.5" size={18} /><span>{STUDIO_INFO.address}<br />{STUDIO_INFO.region}</span></li>
              <li className="flex items-center gap-3"><Phone className="text-gold" size={18} /><a href={`tel:+${STUDIO_INFO.whatsapp}`} className="hover:text-gold">{STUDIO_INFO.phone}</a></li>
              <li className="flex items-center gap-3"><Mail className="text-gold" size={18} /><a href={`mailto:${STUDIO_INFO.email}`} className="hover:text-gold break-all">{STUDIO_INFO.email}</a></li>
            </ul>
            <div className="flex flex-wrap gap-3 mt-6">
              <a href={STUDIO_INFO.maps} target="_blank" rel="noreferrer" className="btn-ghost !py-2.5 text-sm">Itinéraire</a>
              <a href={`tel:+${STUDIO_INFO.whatsapp}`} className="btn-gold !py-2.5 text-sm"><Phone size={16} /> Appeler</a>
            </div>
          </div>
          <div className="min-h-[280px]">
            <iframe title="Carte AL Music Records" src={STUDIO_INFO.mapsEmbed} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </Section>

      {/* DEMANDE PARTICULIÈRE */}
      <SpecialRequest />

      {/* CTA */}
      <section className="relative py-24 border-y border-white/10 overflow-hidden mt-10">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-red-900/10" />
        <Section className="relative z-10 text-center">
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl">Ton prochain titre<br /><span className="text-gold">commence ici.</span></h2>
          <p className="text-slate-300 mt-5 max-w-lg mx-auto">Tu as une chanson à enregistrer ? Réserve ta session chez AL Music Records.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/booking-studio" className="btn-gold">🔥 RÉSERVER MA SESSION</Link>
          </div>
        </Section>
      </section>
    </div>
  );
}
